import { apiClient } from '../utils/api';
import { UrlBuilder } from '../utils/urls';
import { RecordItem } from '../models/RecordItem';
import { codeToSymbol, symbolToCode } from '../utils/currencyUtils';

export interface CreateRecordData {
  record_name: string;
  record_description?: string | null;
  record_currency_code?: string;
  user_id?: number;
  record_creator_id?: number;
  created_at?: string;
}

export interface UpdateRecordData {
  record_name?: string;
  record_description?: string | null;
  record_currency_code?: string;
  user_id: number;
}

export interface FindRecordsFilter {
  id?: number;
  user_id?: number;
  record_name?: string;
  record_currency_code?: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  code?: number;
}

interface FindRecordsResponse {
  message: string;
  records: RecordItem[];
}

interface ExportRecordResponse {
  record_name: string;
  record_description: string;
  currency_code: string;
  transactions: any[];
}

interface RecordUsersResponse {
  record_id: number;
  record_name: string;
  users: Array<{
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    pivot: {
      record_id: number;
      user_id: number;
      can_read: number;
      can_write: number;
    };
  }>;
}

interface GrantPermissionData {
  record_id: number;
  target_user_id: number;
  can_read: boolean;
  can_write: boolean;
}

class RecordsService {
  // Get all records for the current user with token (using POST method)
  async getRecords(token: string, filter?: FindRecordsFilter): Promise<ApiResponse<RecordItem[]>> {
    try {
      const response = await apiClient.postWithToken<FindRecordsResponse>(
        UrlBuilder.records.find(),
        token,
        filter || {}
      );

      if (response.success && response.data) {
        const list = Array.isArray(response.data.records) ? response.data.records : [];
        const recordsWithCurrencyCodes = list.map(record => {
          const recordWithCurrencyCode = {
            ...record,
            record_currency_code: symbolToCode(record.record_currency_code || '')
          };
          return RecordItem.fromJSON(recordWithCurrencyCode);
        });
        
        return {
          success: true,
          data: recordsWithCurrencyCodes
        };
      } else {
        const errorResponse = response as { success: false; error: string; code?: number };
        const emptyList =
          errorResponse.code === 404 &&
          /no records found|record not found/i.test(errorResponse.error || '');
        if (emptyList) {
          return { success: true, data: [] };
        }
        return {
          success: false,
          error: errorResponse.error || 'Failed to fetch records',
          code: errorResponse.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Create a new record with token
  async createRecord(token: string, data: CreateRecordData): Promise<ApiResponse<RecordItem>> {
    try {
      // Convert currency code to symbol for API
      const apiData = {
        ...data,
        record_description: data.record_description ?? '',
        record_currency_code: data.record_currency_code ? codeToSymbol(data.record_currency_code) : undefined
      };
      
      const response = await apiClient.postWithToken<RecordItem & { record?: RecordItem }>(
        UrlBuilder.records.create(),
        token,
        apiData
      );

      if (response.success && response.data) {
        const raw = (response.data as { record?: RecordItem }).record || response.data;
        const recordWithCurrencyCode = {
          ...raw,
          record_currency_code: symbolToCode(raw.record_currency_code || '')
        };
        
        return {
          success: true,
          data: RecordItem.fromJSON(recordWithCurrencyCode)
        };
      } else {
        // When success is false, error and code should exist
        const errorResponse = response as { success: false; error: string; code?: number };
        return {
          success: false,
          error: errorResponse.error || 'Failed to create record',
          code: errorResponse.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Update a record with token
  async updateRecord(token: string, id: number, data: UpdateRecordData): Promise<ApiResponse<RecordItem>> {
    try {
      // Convert currency code to symbol for API
      const apiData = {
        ...data,
        record_description: data.record_description ?? '',
        record_currency_code: data.record_currency_code ? codeToSymbol(data.record_currency_code) : undefined
      };
      
      const response = await apiClient.putWithToken<RecordItem & { record?: RecordItem }>(
        UrlBuilder.records.edit(id),
        token,
        apiData
      );

      if (response.success && response.data) {
        const raw = (response.data as { record?: RecordItem }).record || response.data;
        const recordWithCurrencyCode = {
          ...raw,
          record_currency_code: symbolToCode(raw.record_currency_code || '')
        };
        
        return {
          success: true,
          data: RecordItem.fromJSON(recordWithCurrencyCode)
        };
      } else {
        // When success is false, error and code should exist
        const errorResponse = response as { success: false; error: string; code?: number };
        return {
          success: false,
          error: errorResponse.error || 'Failed to update record',
          code: errorResponse.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Delete a record with token (requires creator_id and verification_code in body)
  async deleteRecord(
    token: string,
    id: number,
    creatorId: number,
    verificationCode: string
  ): Promise<ApiResponse> {
    try {
      const response = await apiClient.deleteWithToken(
        UrlBuilder.records.delete(id),
        token,
        {
          id,
          creator_id: creatorId,
          verification_code: verificationCode,
        }
      );

      if (response.success) {
        return {
          success: true
        };
      } else {
        // When success is false, error and code should exist
        const errorResponse = response as { success: false; error: string; code?: number };
        return {
          success: false,
          error: errorResponse.error || 'Failed to delete record',
          code: errorResponse.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Join a record by code (using PUT method as per Postman)
  async joinRecordByCode(token: string, code: string, userId: number): Promise<ApiResponse<RecordItem>> {
    try {
      const response = await apiClient.putWithToken<RecordItem>(
        UrlBuilder.records.joinByCode(code, userId),
        token,
        {}
      );

      if (response.success && response.data) {
        // Convert currency symbol back to code for frontend and create RecordItem instance
        const recordWithCurrencyCode = {
          ...response.data,
          record_currency_code: symbolToCode(response.data.record_currency_code || '')
        };
        
        return {
          success: true,
          data: RecordItem.fromJSON(recordWithCurrencyCode)
        };
      } else {
        // When success is false, error and code should exist
        const errorResponse = response as { success: false; error: string; code?: number };
        return {
          success: false,
          error: errorResponse.error || 'Failed to join record',
          code: errorResponse.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Export a record with token - returns JSON data
  async exportRecord(token: string, id: number): Promise<ApiResponse<ExportRecordResponse>> {
    try {
      const response = await apiClient.getWithToken<ExportRecordResponse>(
        UrlBuilder.records.export(id),
        token
      );

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        // When success is false, error and code should exist
        const errorResponse = response as { success: false; error: string; code?: number };
        return {
          success: false,
          error: errorResponse.error || 'Failed to export record',
          code: errorResponse.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Import a record with token
  async importRecord(token: string, jsonData: string): Promise<ApiResponse<RecordItem>> {
    try {
      const response = await apiClient.postWithToken<RecordItem>(
        UrlBuilder.records.import(),
        token,
        { json: jsonData }
      );

      if (response.success && response.data) {
        // Convert currency symbol back to code for frontend and create RecordItem instance
        const recordWithCurrencyCode = {
          ...response.data,
          record_currency_code: symbolToCode(response.data.record_currency_code || '')
        };
        
        return {
          success: true,
          data: RecordItem.fromJSON(recordWithCurrencyCode)
        };
      } else {
        // When success is false, error and code should exist
        const errorResponse = response as { success: false; error: string; code?: number };
        return {
          success: false,
          error: errorResponse.error || 'Failed to import record',
          code: errorResponse.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Get users with permissions for a record
  async getRecordUsers(token: string, recordId: number): Promise<ApiResponse<RecordUsersResponse>> {
    try {
      const response = await apiClient.getWithToken<RecordUsersResponse>(
        UrlBuilder.records.getUsers(recordId),
        token
      );

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        // When success is false, error and code should exist
        const errorResponse = response as { success: false; error: string; code?: number };
        return {
          success: false,
          error: errorResponse.error || 'Failed to fetch record users',
          code: errorResponse.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Grant/update permissions for a user on a record
  async grantPermission(token: string, userId: number, data: GrantPermissionData): Promise<ApiResponse> {
    try {
      const response = await apiClient.putWithToken(
        UrlBuilder.records.grantPermission(userId),
        token,
        data
      );

      if (response.success) {
        return {
          success: true
        };
      } else {
        // When success is false, error and code should exist
        const errorResponse = response as { success: false; error: string; code?: number };
        return {
          success: false,
          error: errorResponse.error || 'Failed to grant permissions',
          code: errorResponse.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Remove user from record
  async removeUserFromRecord(token: string, userId: number, recordId: number, targetUserId: number): Promise<ApiResponse> {
    try {
      const response = await apiClient.deleteWithToken(
        UrlBuilder.records.removeUser(userId, recordId, targetUserId),
        token
      );

      if (response.success) {
        return {
          success: true
        };
      } else {
        // When success is false, error and code should exist
        const errorResponse = response as { success: false; error: string; code?: number };
        return {
          success: false,
          error: errorResponse.error || 'Failed to remove user from record',
          code: errorResponse.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }
}

export const recordsService = new RecordsService();

export type { RecordUsersResponse, GrantPermissionData };
